import axios from 'axios'
import { documentContentRequest } from '@/services/api/documentContent'
import { UserType } from '@/services/api/customer'

export type AccreditationApiResponse = {
  data: AccreditationCheckType[]
  meta: {
    canContinue: boolean
    passed: boolean
  }
}

export type AccreditationCheckType = {
  accreditationId: number
  active: boolean
  description: string | null
  expiresAt: string | null
  feedback: string | null
  isStopWork: boolean
  name: string
  referenceId: string
  result: string
  type: string
  updateAllowed: boolean
  userAccreditationId: number
  validFrom: string
}

export type CustomerAccreditationType = {
  id: number
  isThirdParty: boolean
  name: string
  stopWork: boolean
  type: string
  userTypes: UserType[]
  accreditation: AccreditationType
}

/**
 * An accreditation's document, as `UserAccreditationResource` reports it: an identity and a
 * scan state, and nothing else. There is no filename column on `user_accreditations` and no
 * mime column either, so neither is published and neither is guessed at here (B54/B56) — the
 * mime type of an existing document is whatever its content response says it is.
 */
export type UserAccreditationDocumentType = {
  status: string | null
  uuid: string
}

export type UserAccreditationType = {
  accreditationId: number
  active: boolean
  description: string | null
  document: UserAccreditationDocumentType | null
  expiresAt: string | null
  id: number
  isValid: boolean
  referenceId: string | null
  userId: number
  validFrom: string | null
}

export type UserAccreditationRequestType = Record<
  string,
  boolean | Date | File | null | number | string
>

export type AccreditationType = {
  id: number
  name: string
  isThirdParty: boolean
  descriptionRequired: boolean
  referenceIdRequired: boolean
  validFromRequired: boolean
  expiresAtRequired: boolean
  documentRequired: boolean
  updateAllowed: boolean
  displayName: string
  userAccreditations: UserAccreditationType[]
}

export async function checkAccreditations(
  customerId: number,
  accreditations: CustomerAccreditationType[],
  siteId: number,
  contractorCompanyId: number,
  isFinalCheck: boolean,
): Promise<AccreditationApiResponse> {
  const response = await axios.get(`api/accreditationChecks`, {
    params: {
      accreditations: accreditations.map(
        (accreditation) => accreditation.accreditation.id,
      ),
      contractorCompanyId: contractorCompanyId,
      customerId: customerId,
      isFinalCheck: isFinalCheck,
      siteId: siteId,
    },
  })

  return response.data
}

export async function getActiveCustomerAccreditations(
  customerId: number,
): Promise<CustomerAccreditationType[]> {
  const response = await axios.get(`api/customers/${customerId}/accreditations`)

  return response.data.data
}

export async function getAccreditationWithUserAccreditations(
  accreditationIds: number[],
  userAccreditationIds: number[],
): Promise<AccreditationType[]> {
  const response = await axios.get(`api/accreditations`, {
    params: {
      accreditationIds: accreditationIds,
      userAccreditationIds: userAccreditationIds,
    },
  })

  return response.data.data
}

/**
 * Create an accreditation for a user, with the document the user picked for it if there was
 * one: there is no upload call of its own, the file is a field on this one, so the record and
 * its document arrive together and the api can undo the record if storing the file fails.
 */
export async function storeUserAccreditation(
  data: UserAccreditationRequestType,
): Promise<UserAccreditationType> {
  data['active'] = true
  const response = await axios.post(
    `api/users/${data.userId}/accreditations`,
    bodyFor(data),
  )

  return response.data.data
}

/**
 * Update an accreditation, replacing its document when a new file was picked.
 *
 * A file goes out as `POST` with a spoofed `_method`, not as a real `PATCH`: PHP populates
 * `$_FILES` for `POST` only, so a multipart `PATCH` reaches the api with the file missing and
 * fails validation with nothing in the payload to say why. Laravel unspoofs `_method` before
 * routing, so capture-api's `PATCH` route and controller are untouched. Without a file this
 * stays the `PATCH` it has always been.
 */
export async function updateUserAccreditation(
  data: UserAccreditationRequestType,
): Promise<UserAccreditationType> {
  data['active'] = true
  const url = `api/users/${data.userId}/accreditations/${data.userAccreditationId}`

  if (!(data.document instanceof File)) {
    const response = await axios.patch(url, data)

    return response.data.data
  }

  const body = multipartBodyFor(data)
  body.append('_method', 'PATCH')

  const response = await axios.post(url, body)

  return response.data.data
}

export async function fetchAccreditationDocument(
  userId: number,
  userAccreditationId: number,
): Promise<{ content: string; mimeType: string | null }> {
  const response = await axios.get<Blob>(
    `api/users/${userId}/accreditations/${userAccreditationId}/document`,
    documentContentRequest,
  )

  return {
    content: URL.createObjectURL(response.data),
    mimeType: response.data.type || null,
  }
}

/**
 * An accreditation with no document keeps the json body it has always sent: its fields are
 * scalars the api validates the same either way, and encoding every accreditation as multipart
 * to carry a file most of them do not have would be a change for nothing.
 */
function bodyFor(
  data: UserAccreditationRequestType,
): FormData | UserAccreditationRequestType {
  return data.document instanceof File ? multipartBodyFor(data) : data
}

function multipartBodyFor(data: UserAccreditationRequestType): FormData {
  const body = new FormData()

  Object.entries(data).forEach(([field, value]) => {
    if (value instanceof File) {
      body.append(field, value)

      return
    }

    body.append(field, multipartValue(value))
  })

  return body
}

/**
 * Each field as the json body would have sent it, so an accreditation says the same thing
 * whether or not a file went with it: a date as the ISO string `JSON.stringify` writes, no value
 * as an empty string (which Laravel reads as null, where 'null' would be stored as text), and a
 * boolean as '1'/'0', because Laravel's `boolean` rule rejects 'true'.
 */
function multipartValue(
  value: Exclude<UserAccreditationRequestType[string], File>,
): string {
  if (value instanceof Date) {
    return value.toISOString()
  }

  if (value === null) {
    return ''
  }

  if (typeof value === 'boolean') {
    return value ? '1' : '0'
  }

  return String(value)
}
